import ApiService from "@/helpers/api.service.v6lite";
import moment from "moment";
import { Storage } from "aws-amplify";

const APIV6 = new ApiService();
const S3_FOLDER = "eload-lite-category";

// ─────────────────────────────────────────────────────────────────────────────
//  STATE
// ─────────────────────────────────────────────────────────────────────────────

export const state = {
    categoryList: [],
    categoryPageInfo: {
        total: 0,
    },
    categoryDetails: null,
    categoryLogs: [],
    categoryLogsTotal: 0,
};

// ─────────────────────────────────────────────────────────────────────────────
//  MUTATIONS
// ─────────────────────────────────────────────────────────────────────────────

export const mutations = {
    UPDATE_CATEGORY_LIST(state, list) {
        state.categoryList = list;
    },
    UPDATE_CATEGORY_PAGE_INFO(state, total) {
        state.categoryPageInfo.total = total;
    },
    UPDATE_CATEGORY_DETAILS(state, details) {
        state.categoryDetails = details;
    },
    UPDATE_CATEGORY_LOGS(state, logs) {
        state.categoryLogs = logs;
    },
    UPDATE_CATEGORY_LOGS_TOTAL(state, total) {
        state.categoryLogsTotal = total;
    },
};

// ─────────────────────────────────────────────────────────────────────────────
//  ACTIONS
// ─────────────────────────────────────────────────────────────────────────────

export const actions = {
    /**
     * Get paginated category list.
     * Calls POST /eload/category/list-category
     */
    getLiteCategoryList(context, params) {
        return new Promise((resolve, reject) => {
            APIV6.post(`/eload/category/list-category`, params)
                .then(({ data }) => {
                    context.commit('UPDATE_CATEGORY_LIST', data.message.rows);
                    context.commit('UPDATE_CATEGORY_PAGE_INFO', data.message.pageInfo.total);
                    resolve(data);
                })
                .catch((error) => {
                    reject(error.response ? error.response.data : error);
                });
        });
    },

    /**
     * Generic category action: create | update | delete | toggle-status
     * Calls POST /eload/category/:action
     * @param {object} params - { action: string, body: object }
     */
    actionLiteCategory(context, params) {
        return new Promise((resolve, reject) => {
            APIV6.post(`/eload/category/${params.action}`, params.body)
                .then(({ data }) => {
                    if (data.status === 'ok') {
                        resolve(data);
                    } else {
                        // reject full payload so caller can inspect status/statusCode
                        reject(data);
                    }
                })
                .catch((error) => {
                    const errData = error.response ? error.response.data : error;
                    // preserve full error object
                    reject(errData);
                });
        });
    },

    /**
     * Upload special category icon 2 to S3.
     * @param {object} params - { filename, type, base64 }
     * Resolves with the S3 key (url).
     */
    uploadLiteSpecialCategoryIcon2(context, params) {
        return new Promise((resolve, reject) => {
            const allowedType = ['svg+xml'];
            if (params.type && !allowedType.includes(params.type.toLowerCase())) {
                reject('Filetype not allowed');
                return;
            }

            const metadata = {
                customPrefix: { public: '' },
                expires: moment().add(365, 'days').toDate(),
            };

            if (params.type === 'svg+xml') {
                metadata['contentType'] = 'image/svg+xml';
            }

            const s3_loc = `${S3_FOLDER}/icons/${params.filename}`;

            Storage.put(s3_loc, params.base64, metadata)
                .then((stored) => {
                    resolve(stored.key);
                })
                .catch((err) => {
                    console.log(err);
                    reject(err);
                });
        });
    },

    /**
     * Get category audit logs.
     * Calls POST /eload/category/list-category-audit-logs
     * @param {object} params - { categoryId, page, rows, sort, orderDesc, search, searchField, dateRange }
     */
    getLiteCategoryAuditLogs(context, params) {
        return new Promise((resolve, reject) => {
            APIV6.post(`/eload/category/list-category-logs`, params)
                .then(({ data }) => {
                    context.commit('UPDATE_CATEGORY_LOGS', data.message.rows);
                    context.commit('UPDATE_CATEGORY_LOGS_TOTAL', data.message.pageInfo.total);
                    resolve(data);
                })
                .catch((error) => {
                    reject(error.response ? error.response.data : error);
                });
        });
    },

    /**
     * Export category audit logs.
     * Calls POST /eload/category/export-category-audit-logs
     * @param {object} params - { categoryId, email, search, searchField, dateRange }
     */
    exportLiteCategoryAuditLogs(context, params) {
        return new Promise((resolve, reject) => {
            APIV6.post(`/eload/category/export-category-logs`, params)
                .then(({ data }) => {
                    resolve(data);
                })
                .catch((error) => {
                    reject(error.response ? error.response.data : error);
                });
        });
    },
};

// ─────────────────────────────────────────────────────────────────────────────
//  GETTERS
// ─────────────────────────────────────────────────────────────────────────────

export const getters = {
    liteCategoryList: (state) => state.categoryList,
    liteCategoryTotal: (state) => state.categoryPageInfo.total,
    liteCategoryDetails: (state) => state.categoryDetails,
    liteCategoryLogs: (state) => state.categoryLogs,
    liteCategoryLogsTotal: (state) => state.categoryLogsTotal,
};
